import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { Badge } from "@/shared/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { toast } from "sonner";
import { Award, User, Calendar, Clock, FileText } from "lucide-react";

interface Competency {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_global: boolean;
}

interface FactoryUser {
  id: string;
  full_name: string | null;
  email: string;
  job_title: string | null;
}

interface AssignCompetencyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  users: FactoryUser[];
  competencies: Competency[];
  tenantId: string | null;
  factoryId: string;
  preselectedUserId: string | null;
  currentUserId: string | undefined;
  onSuccess: () => void;
}

export function AssignCompetencyDialog({
  open,
  onOpenChange,
  users,
  competencies,
  tenantId,
  factoryId,
  preselectedUserId,
  currentUserId,
  onSuccess,
}: AssignCompetencyDialogProps) {
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedCompetencyId, setSelectedCompetencyId] = useState<string>("");
  const [certifiedAt, setCertifiedAt] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [expiresAt, setExpiresAt] = useState<string>("");
  const [trainingHours, setTrainingHours] = useState<string>("");
  const [certificateNumber, setCertificateNumber] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  useEffect(() => {
    if (preselectedUserId) {
      setSelectedUserId(preselectedUserId);
    }
  }, [preselectedUserId]);

  useEffect(() => {
    if (open) {
      setCertifiedAt(new Date().toISOString().split("T")[0]);
      setExpiresAt("");
      setTrainingHours("");
      setCertificateNumber("");
      setNotes("");
      if (!preselectedUserId) {
        setSelectedUserId("");
      }
      setSelectedCompetencyId("");
    }
  }, [open, preselectedUserId]);

  const assignMutation = useMutation({
    mutationFn: async () => {
      if (!tenantId) {
        throw new Error("No tenant selected");
      }

      const { error } = await supabase.schema('core').from("user_competencies").insert({
        user_id: selectedUserId,
        competency_id: selectedCompetencyId,
        factory_id: factoryId,
        tenant_id: tenantId,
        certified_at: certifiedAt,
        expires_at: expiresAt || null,
        certified_by: currentUserId,
        training_hours: trainingHours ? parseFloat(trainingHours) : null,
        certificate_number: certificateNumber || null,
        notes: notes || null,
        status: "active",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Competency assigned successfully");
      onOpenChange(false);
      onSuccess();
    },
    onError: (error: Error & { code?: string }) => {
      if (error.code === "23505") {
        toast.error("This user already has this competency assigned");
      } else {
        toast.error("Failed to assign competency");
        console.error(error);
      }
    },
  });

  const selectedCompetency = competencies.find((c) => c.id === selectedCompetencyId);
  const selectedUser = users.find((u) => u.id === selectedUserId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            Assign Competency
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* User Selection */}
          <div>
            <Label htmlFor="user" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Team Member
            </Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder="Select team member" />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    <div className="flex flex-col">
                      <span>{user.full_name || user.email.split("@")[0]}</span>
                      <span className="text-xs text-muted-foreground">
                        {user.job_title || user.email}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Competency Selection */}
          <div>
            <Label htmlFor="competency" className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              Competency
            </Label>
            <Select value={selectedCompetencyId} onValueChange={setSelectedCompetencyId}>
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder="Select competency" />
              </SelectTrigger>
              <SelectContent>
                {competencies.map((comp) => (
                  <SelectItem key={comp.id} value={comp.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: comp.color }}
                      />
                      <span>{comp.name}</span>
                      {comp.is_global && (
                        <Badge variant="outline" className="text-[10px] ml-1">
                          Global
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedCompetency?.description && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {selectedCompetency.description}
              </p>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="certifiedAt" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Certified Date
              </Label>
              <Input
                id="certifiedAt"
                type="date"
                value={certifiedAt}
                onChange={(e) => setCertifiedAt(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="expiresAt" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Expires (Optional)
              </Label>
              <Input
                id="expiresAt"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="mt-1.5"
              />
            </div>
          </div>

          {/* Additional Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="trainingHours">Training Hours</Label>
              <Input
                id="trainingHours"
                type="number"
                step="0.5"
                min="0"
                value={trainingHours}
                onChange={(e) => setTrainingHours(e.target.value)}
                placeholder="e.g., 8"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="certificateNumber">Certificate #</Label>
              <Input
                id="certificateNumber"
                value={certificateNumber}
                onChange={(e) => setCertificateNumber(e.target.value)}
                placeholder="Optional"
                className="mt-1.5"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Notes (Optional)
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this certification..."
              rows={2}
              className="mt-1.5"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => assignMutation.mutate()}
            disabled={
              !selectedUserId ||
              !selectedCompetencyId ||
              !certifiedAt ||
              assignMutation.isPending
            }
          >
            {assignMutation.isPending ? "Assigning..." : "Assign Competency"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
